import { useCallback, useState } from 'react';
import { noteCommentPath, noteCommentsPath } from '@bms2/shared';
import { del, post } from './api';

/**
 * 🔒 The two writes a note's thread makes (decision 0671), for the record page's
 * Internal Notes tile and the document page's panel alike: each answers the
 * server's refusal in words, or null once it has landed and the page has read
 * its notes again.
 */
export function useNoteReplies(apiBaseUrl: string, reload: () => void) {
  const onNoteReply = useCallback(async (noteId: string, body: string): Promise<string | null> => {
    const answer = await post(noteCommentsPath(noteId), { body }, apiBaseUrl);
    if (!answer.ok) return answer.message;
    reload();
    return null;
  }, [apiBaseUrl, reload]);
  const onNoteReplyDelete = useCallback(async (noteId: string, replyId: string): Promise<string | null> => {
    const answer = await del(noteCommentPath(noteId, replyId), apiBaseUrl);
    if (!answer.ok) return answer.message;
    reload();
    return null;
  }, [apiBaseUrl, reload]);
  /** The threads open on this page, kept here so reading the notes again after a reply leaves the thread open. */
  const [openThreads, setOpenThreads] = useState<ReadonlySet<string>>(new Set());
  const onToggleThread = useCallback((noteId: string) => {
    setOpenThreads((held) => {
      const next = new Set(held);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  }, []);
  return { onNoteReply, onNoteReplyDelete, openThreads, onToggleThread };
}
